import { useLayoutEffect, useRef, useState } from 'react'
import type { ClusterInput, DriftStatusKind, SpeciesItem } from '../api'
import {
  SIZE_OPTIONS,
  type SizeClass,
  halfHourSlots,
  localDateStr,
  localSlot,
  composeLocal,
} from './clusterDraft'
import { parseCoord, validLat, validLon, formatCoord } from './coords'
import { speciesName, useLang, useT } from '../i18n'

/** Дрейф выбранной точки (для показа при редактировании). */
export interface PointDrift {
  status: DriftStatusKind
  ageDays: number
  currentLon: number
  currentLat: number
  /** Подпись к координатам: «сейчас» либо «на 18 июля» — ползунок дней. */
  atLabel: string
  /** Позиции на выбранный день нет — показана последняя известная. */
  frozen?: boolean
}

interface ClusterFormProps {
  /** Точка клика: lng/lat (координаты косяка) + пиксели x/y (позиция формы). */
  point: { lng: number; lat: number; x: number; y: number }
  species: SpeciesItem[]
  initialSpeciesId: number | null
  initialObservedAt: Date
  initialSize?: SizeClass | null
  initialNote?: string
  /** Режим правки существующей точки (иначе — создание новой). */
  editing?: boolean
  /** Дрейф точки (только в режиме правки); null — ещё не рассчитан. */
  drift?: PointDrift | null
  /** Может быть async: пока сохранение идёт (бэкенд синхронно считает дрейф
   *  точки, ~1–2с), форма показывает лоадер. */
  onSubmit: (body: ClusterInput) => void | Promise<void>
  onCancel: () => void
  /** Удалить точку (только в режиме правки). */
  onDelete?: () => void
  /** Право на удаление (автор точки или владелец кабинета, см. can_delete).
   *  По умолчанию true — сервер всё равно проверяет сам. */
  canDelete?: boolean
}

const inputCls =
  'w-full rounded-lg border border-hull bg-trench px-2.5 py-1.5 text-sm text-foam placeholder:text-mist focus:border-sonar focus:outline-none'

const SLOTS = halfHourSlots()

// Статус дрейфа точки: ключ подписи + цвет.
const DRIFT_LABEL: Record<DriftStatusKind, { key: string; cls: string }> = {
  active: { key: 'models.drift.point.active', cls: 'text-sonar' },
  beached: { key: 'models.drift.point.beached', cls: 'text-buoy' },
  gap: { key: 'models.drift.point.gap', cls: 'text-faint' },
  static: { key: 'models.drift.point.static', cls: 'text-faint' },
}

export function ClusterForm({
  point,
  species,
  initialSpeciesId,
  initialObservedAt,
  initialSize = null,
  initialNote = '',
  editing = false,
  drift = null,
  onSubmit,
  onCancel,
  onDelete,
  canDelete = true,
}: ClusterFormProps) {
  const [speciesId, setSpeciesId] = useState<number | ''>(initialSpeciesId ?? '')
  const [dateStr, setDateStr] = useState(() => localDateStr(initialObservedAt))
  const [slot, setSlot] = useState(() => localSlot(initialObservedAt))
  const [size, setSize] = useState<SizeClass | null>(initialSize)
  const [note, setNote] = useState(initialNote)
  // Сохранение идёт: бэкенд синхронно сносит точку по течениям (~1–2с). Без
  // индикатора казалось, что форма зависла (жалоба капитана).
  const [submitting, setSubmitting] = useState(false)

  // Координаты правятся вручную: клик по карте — грубая наводка, точная отметка
  // приходит из журнала цифрами. Форма пересоздаётся на каждый клик (key в
  // ModelsPage), поэтому инициализации из point достаточно.
  const [latStr, setLatStr] = useState(() => formatCoord(point.lat))
  const [lonStr, setLonStr] = useState(() => formatCoord(point.lng))
  const lat = parseCoord(latStr)
  const lon = parseCoord(lonStr)
  const latOk = validLat(lat)
  const lonOk = validLon(lon)

  // Форма появляется у точки клика (req 2), но не вылезает за края карты.
  const formRef = useRef<HTMLFormElement>(null)
  const [pos, setPos] = useState({ left: point.x, top: point.y })
  useLayoutEffect(() => {
    const el = formRef.current
    const parent = el?.offsetParent as HTMLElement | null
    if (!el || !parent) return
    const pad = 8
    const maxLeft = parent.clientWidth - el.offsetWidth - pad
    const maxTop = parent.clientHeight - el.offsetHeight - pad
    setPos({
      left: Math.max(pad, Math.min(point.x, maxLeft)),
      top: Math.max(pad, Math.min(point.y, maxTop)),
    })
  }, [point.x, point.y])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!size || submitting) return
    // При правке координаты не редактируются (бэкенд их не меняет) — шлём исходные.
    if (!editing && !(latOk && lonOk)) return
    setSubmitting(true)
    try {
      await onSubmit({
        lat: editing ? point.lat : lat!,
        lon: editing ? point.lng : lon!,
        species_id: speciesId === '' ? null : Number(speciesId),
        size_class: size,
        observed_at: composeLocal(dateStr, slot).toISOString(),
        note: note || null,
      })
      // при успехе форму размонтирует родитель (setNewPoint/setEditPoint → null)
    } catch {
      setSubmitting(false) // ошибка сохранения — вернуть возможность повторить
    }
  }

  const dr = drift ? DRIFT_LABEL[drift.status] : null
  const t = useT()
  const { lang } = useLang()

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      style={{ left: pos.left, top: pos.top }}
      className="pointer-events-auto absolute w-64 space-y-2 rounded-xl border border-hull bg-abyss/95 p-3 shadow-console backdrop-blur"
    >
      <div className="text-sm font-semibold text-foam">
        {editing ? t('models.cluster.title') : t('models.cluster.newTitle')}
      </div>
      {editing ? (
        <div className="tnum text-xs text-mist">
          {point.lat.toFixed(3)}, {point.lng.toFixed(3)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-[11px] text-mist">{t('common.lat')}</span>
            <input
              type="text"
              inputMode="decimal"
              value={latStr}
              onChange={e => setLatStr(e.target.value)}
              aria-label={t('common.lat')}
              aria-invalid={!latOk}
              className={`${inputCls} tnum ${latOk ? '' : 'border-buoy focus:border-buoy'}`}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] text-mist">{t('common.lon')}</span>
            <input
              type="text"
              inputMode="decimal"
              value={lonStr}
              onChange={e => setLonStr(e.target.value)}
              aria-label={t('common.lon')}
              aria-invalid={!lonOk}
              className={`${inputCls} tnum ${lonOk ? '' : 'border-buoy focus:border-buoy'}`}
            />
          </label>
        </div>
      )}
      {!editing && !(latOk && lonOk) && (
        <div className="text-xs text-buoy">
          {t('models.cluster.coordHint')}
        </div>
      )}

      {/* Дрейф точки (только при правке): статус + возраст + текущая позиция. */}
      {editing && dr && drift && (
        <div className="rounded-lg border border-hull bg-trench/60 px-2.5 py-1.5 text-xs">
          <div>
            {t('models.cluster.driftLabel')}{' '}
            <span className={`font-medium ${dr.cls}`}>{t(dr.key)}</span>
            <span className="text-faint"> · {t('common.days', { count: drift.ageDays })}</span>
          </div>
          <div className="tnum mt-0.5 text-mist">
            {drift.atLabel}: {drift.currentLat.toFixed(3)}, {drift.currentLon.toFixed(3)}
            {drift.frozen && (
              <span className="text-buoy"> · {t('models.drift.point.lastKnown')}</span>
            )}
          </div>
        </div>
      )}

      <select
        value={speciesId}
        onChange={e => setSpeciesId(e.target.value === '' ? '' : Number(e.target.value))}
        className={inputCls}
      >
        <option value="">{t('models.cluster.speciesOptional')}</option>
        {species.map(s => (
          <option key={s.species_id} value={s.species_id}>
            {speciesName(s, lang)}
          </option>
        ))}
      </select>

      <div className="flex gap-2">
        <input
          type="date"
          value={dateStr}
          onChange={e => setDateStr(e.target.value)}
          className={`${inputCls} tnum [color-scheme:dark]`}
        />
        <select value={slot} onChange={e => setSlot(e.target.value)} className={`${inputCls} tnum w-24`}>
          {SLOTS.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div className="mb-1 text-xs text-mist">{t('models.cluster.sizeTitle')}</div>
        <div className="grid grid-cols-3 gap-1">
          {SIZE_OPTIONS.map(o => (
            <button
              key={o.value}
              type="button"
              onClick={() => setSize(o.value)}
              className={
                'rounded-lg border px-2 py-1.5 text-sm ' +
                (size === o.value
                  ? 'border-sonar bg-sonar/20 font-semibold text-foam'
                  : 'border-hull bg-trench text-mist hover:text-foam')
              }
            >
              {t(o.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <input
        type="text"
        placeholder={t('models.cluster.notePlaceholder')}
        value={note}
        onChange={e => setNote(e.target.value)}
        className={inputCls}
      />

      {/* flex-wrap: три кнопки в ряд не помещались в ширину формы, и
          «Сохранить» вылезала за её край. */}
      <div className="flex flex-wrap items-center gap-2">
        {editing && onDelete && canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="rounded-lg px-2 py-1 text-sm text-buoy hover:bg-buoy/10"
          >
            {t('common.delete')}
          </button>
        )}
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-lg px-3 py-1 text-sm text-mist hover:bg-trench hover:text-foam disabled:opacity-40"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !size || (!editing && !(latOk && lonOk))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-sonar to-sonar-dim px-3 py-1 text-sm font-semibold text-abyss shadow-sonar disabled:opacity-60"
          >
            {submitting && (
              <svg
                className="h-3.5 w-3.5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
              </svg>
            )}
            {submitting ? t('models.cluster.computingDrift') : t('common.save')}
          </button>
        </div>
      </div>
    </form>
  )
}
